import { ISortOrder } from './../sort-toggle/sort-toggle.component';
import { Component, OnInit, ViewChild } from '@angular/core';
import * as moment from 'moment';
import swal from 'sweetalert2/dist/sweetalert2.js';
import { BaseComponent } from '../base/base.component';
import { Class } from '../data/class';
import { Course } from '../data/course';
import { OrganizationClassSection, OrganizationDetail } from '../data/organization-detail';
import { Student } from '../data/student';
import { Teacher } from '../data/teacher';
import { AddOrEditClassModalComponent } from '../add-or-edit-class-modal/add-or-edit-class-modal.component';
import { StudentLevelReassignment } from '../data/student-level-reassignment';

enum Page
{
    Students,
    Staff,
    Archive,
    UsageLimits,
    Assignment,
    AssignmentReport,
    Assessments,
    Data
}

@Component({
    selector: 'app-class-detail',
    templateUrl: './class-detail.component.html',
    styleUrls: ['./class-detail.component.scss']
})
export class ClassDetailComponent extends BaseComponent implements OnInit
{
    courses: Course[];
    Page = Page;

    addItems = [
        {
            key: Page.Students,
            description: 'New Student',
        },
        {
            key: Page.Students,
            existing: true,
            description: 'Existing Student',
        },
        {
            key: Page.Staff,
            description: 'Staff',
        },
        {
            key: Page.Archive,
            description: 'Section',
        },
        {
            key: Page.UsageLimits,
            description: 'Usage'
        },
        {
            key: Page.Assignment,
            description: 'Assignment'
        },
        {
            key: Page.AssignmentReport,
            description: 'Report'
        },
        {
            key: Page.Assessments,
            description: 'Assessments'
        },
        {
            key: Page.Data,
            description: 'Data'
        }
    ];

    organization: OrganizationDetail;
    organizationId: number;
    previousTab: string;
    class: Class;
    classError: boolean = false;
    classId: number;
    classes: Class[];
    showingPage: Page;
    students: Student[];
    teacher: Teacher;
    teacherId: number;
    @ViewChild('newStartingOnField', { static: false })
    newStartingOnField: any;
    newClass: Class = Class.getNew();
    @ViewChild('newClassTooltip', { static: false })
    newClassTooltip: any;
    @ViewChild('classTeachersListComponent', { static: false })
    classTeachersListComponent: any;
    selectableCourses: Course[];
    selectableCoursesForNewStudent: Course[];
    @ViewChild('editClassModal', { static: false })
    editClassModal: AddOrEditClassModalComponent;
    assignmentPDF: string;
    courseForEditStudent: Course;
    batchStudentUpdates: any;

    hasStudents(): boolean { return Array.isArray(this.students) && this.students.length > 0; }
    hasTeachers(): boolean { return Array.isArray(this.class.Teachers) && this.class.Teachers.length > 0; }
    hasPassCodes(): boolean { return Array.isArray(this.students) && this.students.some(student => !!student.PassCode) }

    get CourseCode(): string
    {
        return this.organization.ClassSection.CourseCode;
    }
    get CourseDescription(): string
    {
        return this.organization.ClassSection.CourseDescription;
    }

    ngOnInit()
    {
        this.getCourses();
        this.route.params.subscribe(params =>
        {
            if (!params.organizationId)
            {
                this.route.queryParams.subscribe(queryParams =>
                {
                    this.organization = {
                        ID: null,
                        ExternalID: '',
                        LoginCode: '',
                        Description: '',
                        TypeCode: 'CS',
                        TypeDescription: '',
                        Administrators: [],
                        ClassSection: {
                            CourseCode: '',
                            CourseDescription: ''
                        },
                        ParentOrganizationID: queryParams.parentId,
                        ParentOrganizationDescription: queryParams.parentDescription,
                    };
                });
                this.class = Class.getNew();
                this.editObj = this.organization;
            }
            else
            {
                this.organizationId = params.organizationId;
                if (!this.organization)
                {
                    this.getOrganizationDetail();
                    this.route.queryParams.subscribe(queryParams =>
                    {
                        if (queryParams.t)
                        {
                            this.showingPage = (<any>Page)[queryParams.t];
                        }
                        else
                        {
                            this.showingPage = Page.Students;
                        }
                        if (queryParams.pt)
                        {
                            this.previousTab = queryParams.pt;
                        }
                    });
                }
                if (params.classId && params.classId !== 'latest' && params.classId != this.classId)
                {
                    this.classId = +params.classId;
                    this.selectClass();
                }
            }
        });
    }

    editClass()
    {
        this.editClassModal.openForEdit(this.organization);
    }

    deleteClass(): void
    {
        this.dataService.promptDelete('This will permanently delete the class (' + this.class.Description + ').')
            .then(() =>
            {
                this.deleteObj = this.organization;
                this.dataService.deleteOrganization(this.organizationId).subscribe(() =>
                {
                    this.organizationService.deleteOrg(this.organization);
                    this.router.navigate([`/organizations/${this.organization.ParentOrganizationID || ''}`]);
                    // is there any reason to delete classes if we delete the org?
                    // this.dataService.deleteClass(this.class.ID).subscribe(() => {});
                });
            });
    }

    //I couldn't figure out how to make function overloading work, so I did this for now.
    deleteClassTwoArgs(event: Event, theClass: Class): void
    {
        event.stopPropagation();
        if (this.classes.length > 1)
        {
            this.dataService.promptDelete('This will permanently delete this class.')
                .then(() =>
                {
                    this.deleteObj = theClass;
                    this.dataService.deleteClass(theClass.ID).subscribe(() =>
                    {
                        this.deleteObj = null;
                        this.classes = this.classes.filter(s => s.ID !== theClass.ID);
                        if (this.class.ID === theClass.ID)
                        {
                            this.switchTab(Page.Archive, this.classes[0].ID);
                        }
                    });
                });
        }
        else
        {
            this.deleteClass();
        }
    }

    deleteStudent(event: Event, student: Student): void
    {
        this.dataService.promptDelete('This will remove ' + student.Name + ' from the class.', 'Remove')
            .then(() =>
            {
                this.deleteObj = student;
                this.dataService.deleteStudentFromClass(student.ID, this.class.ID).subscribe(response =>
                {
                    this.students.splice(this.students.indexOf(student), 1);
                    this.class.StudentCount = this.students.length;
                    this.deleteObj = null;
                });
            });
        event.stopPropagation();
    }

    getClasses(): void
    {
        this.dataService.getClasses(this.organization.ID).subscribe(classes =>
        {
            this.classes = classes;
            this.sortClasses();
            this.classes.forEach(c =>
            {
                this.setClassEnded(c);
            });
            this.selectClass();
            if (!this.class)
            {
                // we have a class org with no classes.
                this.class = Class.getNew();
                this.editObj = this.organization;
                this.classError = true;
            }
        });
    }

    getCourses()
    {
        if (!this.courses)
        {
            this.dataService.getCourses().subscribe(courses =>
            {
                this.courses = Course.Sort(courses);
                this.getCoursesDone();
            });
        }
        else
        {
            this.getCoursesDone();
        }
    }

    getCoursesDone()
    {
        this.selectableCourses = [
            {
                Code: 'Default',
                Description: 'Select course',
                ShortDescription: 'Select course',
                NumberOnly: 0
            }
        ];
        this.selectableCourses = this.courses;

        this.selectableCoursesForNewStudent = new Array<Course>();
        this.selectableCourses.forEach(course =>
        {
            this.selectableCoursesForNewStudent.push(course);
            //Don't add OlderBeginner2ndHalf to the list of courses for new students because the createStudent endpoint doesn't support it
        });

        if (this.authService.isSuper())
        {
            var ob2Course = {
                Code: 'OlderBeginner2ndHalf',
                Description: 'Older Beginner - 2nd Half',
                ShortDescription: 'Older Beginner - 2nd Half',
                NumberOnly: 13
            };
            this.selectableCourses.push(ob2Course);
        }
    }

    getOrganizationDetail(): void
    {
        this.dataService.getOrganizationDetail(this.organizationId).subscribe(org =>
        {
            this.organization = org;
            if (!org.ClassSection)
            {
                org.ClassSection = new OrganizationClassSection();
                org.ClassSection.CourseCode = '';
            }
            this.getClasses();
            this.setPDFLevel();
        });
    }

    getStudents(): void
    {
        this.students = null;
        this.dataService.getStudents(true, this.class.ID).subscribe(students =>
        {
            this.students = students;
            this.updateSort({ orderBy: "Name", ascending: true });
        });
    }

    updateSort(sortOrder: ISortOrder)
    {
        this.students.sort((a, b) =>
        {
            var sortVal = (a.Name.localeCompare(b.Name));
            if (!sortOrder.ascending) sortVal *= -1;
            return sortVal;
        });
        this.batchStudentUpdates = this.students.map(student => ({
            studentId: student.ID,
            oldCourse: student.ReassignmentCourse === 'OlderBeginner' && student.ReassignmentUnit === 13 ? 'OlderBeginner2ndHalf' : student.ReassignmentCourse || student.CurrentCourse,
            newCourse: student.ReassignmentCourse === 'OlderBeginner' && student.ReassignmentUnit === 13 ? 'OlderBeginner2ndHalf' : student.ReassignmentCourse || student.CurrentCourse,
        }));
    }

    isCurrentClassOver()
    {
        return this.class && this.classes[0] && this.class.ID === this.classes[0].ID && moment(this.class.Ending).isBefore(new Date());
    }

    save(): void
    {
        this.dataService.updateTeacher(this.teacher).subscribe(() => this.goBack());
    }

    selectClass()
    {
        if (this.classes && this.classes.length)
        {
            if (this.classId)
            {
                this.classes.forEach(theClass =>
                {
                    if (theClass.ID === this.classId)
                    {
                        this.class = theClass;
                        this.organization.ClassSection.ClassID = theClass.ID;
                        this.organization.ClassSection.StartingOn = theClass.StartingOn;
                        this.organization.ClassSection.Ending = theClass.Ending;
                        this.organization.ClassSection.CourseDescription = theClass.CourseDescription;
                        this.getStudents();
                    }
                });
                if (!this.class)
                {
                    this.setDefaultClass();
                }
            }
            else
            {
                this.setDefaultClass();
            }
        }
    }

    setPDFLevel(): void
    {
        switch (this.CourseCode)
        {
            case "Y6-EFL":
                this.assignmentPDF = null;
                break;
            case "Y5-EFL":
                this.assignmentPDF = null;
                break;
            default:
                this.assignmentPDF = "https://alegra-learning.s3.us-west-2.amazonaws.com/Joy+School+English_Activity+Reference+Guide-L1.pdf";
        }
    }

    setDefaultClass()
    {
        this.class = this.classes[0];
        this.router.navigate([`/classes/${this.organizationId}/class/${this.class.ID}`], { replaceUrl: true });
    }

    setExpiration(value: string)
    {
        if (!isNaN(Date.parse(value)))
            this.editObj['ExpirationDate'] = Date.parse(value);
    }

    setClassEnded(theClass: Class)
    {
        theClass.Ended = moment(theClass.Ending).add(1, 'd').isBefore(new Date());
    }

    sortClasses()
    {
        this.classes = this.classes.sort((a, b) => a.Ending > b.Ending ? -1 : 1);
    }

    switchTab(page: Page, classId = null)
    {
        if (!classId)
        {
            classId = this.classId;
        }
        this.router.navigate([`/classes/${this.organization.ID}/class/${classId}`], { queryParams: { t: Page[page] } });
    }

    //I couldn't figure out how to make function overloading work, so I did this for now.
    updateClassTwoArgs(event: Event, theClass: Class): void
    {
        if (!this.validateClassDates(theClass))
        {
            return;
        }
        this.startSaveObj(event);
        this.dataService.updateClass(theClass).subscribe(updatedClass =>
        {
            if (updatedClass)
            {
                this.setClassEnded(updatedClass);
                this.classes = this.classes.map(s =>
                {
                    if (s.ID === updatedClass.ID)
                    {
                        updatedClass.StudentCount = s.StudentCount;
                        return updatedClass;
                    }
                    return s;
                });
                if (this.class.ID === updatedClass.ID)
                {
                    this.class = updatedClass;
                }
                this.handleSaveSuccess();
            }
            else
            {
                this.handleSaveError();
            }
        });
    }

    updateStudent(event: Event): void
    {
        this.startSaveObj(event);
        var student = this.editObj as Student;
        var assignedCourse = student.ReassignmentCourse ? student.ReassignmentCourse : student.CurrentCourse;
        if (this.courseForEditStudent.Code !== assignedCourse)
        {
            student.ReassignmentCourse = this.courseForEditStudent.Code;
            student.ReassignmentUnit = 1;
        }

        if (student.ReassignmentCourse === 'OlderBeginner2ndHalf')
        {
            student.ReassignmentCourse = 'OlderBeginner';
            student.ReassignmentUnit = 13;
        }
        console.log(this.editObj as Student);

        console.log(student);

        this.dataService.updateStudent(this.editObj as Student).subscribe(student =>
        {
            if (student)
            {
                this.handleSaveSuccess();
            }
            else
            {
                this.handleSaveError();
            }
        });
    }

    validateClassDates(theClass: Class): boolean
    {
        if (!this.validateDate(theClass.StartingOn))
        {
            swal.fire('Oops!', 'Start date must be a valid date (yyyy-mm-dd).', 'error');
            return false;
        }
        if (!this.validateDate(theClass.Ending))
        {
            swal.fire('Oops!', 'End date must be a valid date (yyyy-mm-dd).', 'error');
            return false;
        }
        if (moment(theClass.StartingOn).isAfter(moment(theClass.Ending)))
        {
            swal.fire('Oops!', 'End date must be after start date.', 'error');
            return false;
        }
        return true;
    }

    // intercept the student's course at the time of edit press
    beginEditStudent(event: Event, student: Student): void
    {
        this.courseForEditStudent = this.selectableCourses.filter(x => x.Code === this.getCourseCodeForStudent(student))[0];
        this.startEditObj(event, student);
    }

    beginBatchEditStudents(event: Event): void
    {
        this.startEditObj(event, this.batchStudentUpdates);
    }

    batchUpdateStudents(event: Event)
    {
        let changes: StudentLevelReassignment[] = this.batchStudentUpdates.filter(update => update.oldCourse !== update.newCourse).map(update => ({
            StudentID: update.studentId,
            CourseCode: update.newCourse === 'OlderBeginner2ndHalf' ? 'OlderBeginner' : update.newCourse,
            UnitNumber: update.newCourse === 'OlderBeginner2ndHalf' ? 13 : 1
        }));

        this.dataService.batchReassignStudentLevels(changes, this.class.ID).subscribe(result =>
        {
            if (result)
            {
                this.batchStudentUpdates.forEach(update =>
                {
                    this.students.find(student => student.ID === update.studentId).ReassignmentCourse = update.newCourse;
                    update.oldCourse = update.newCourse;
                });
                this.handleSaveSuccess();
            }
            else
            {
                this.handleSaveError();
            }
        });
    }

    cancelBatchUpdate(event: Event)
    {
        this.batchStudentUpdates.forEach(update => update.newCourse = update.oldCourse);
        this.cancelEditObj(event, this.batchStudentUpdates);
    }

    getCourseDisplayForStudent(student: Student): string
    {
        return this.selectableCourses.filter(x => x.Code === this.getCourseCodeForStudent(student))[0].ShortDescription;
    }

    getCourseCodeForStudent(student: Student): string
    {
        // The ?? operator sure would be nice:
        // return this.ReassignmentCourse ?? this.CurrentCourse ?? "PlacementTest";
        var course = student.ReassignmentCourse;
        if (this.authService.isSuper() && course === 'OlderBeginner' && student.ReassignmentUnit === 13) course = 'OlderBeginner2ndHalf';
        if (course == null) course = student.CurrentCourse;
        if (course == null) course = "PlacementTest";
        return course;
    }
}
